import * as React from 'react';

/* eslint-disable webflow/no-dangerous-imports */
import useFuzzySearch, {
  ItemActions,
} from '@packages/systems/ui/hooks/useFuzzySearch';

import {FinePrint} from '@packages/systems/design-system/Text';
import {chainFunctions} from '@packages/utilities/component';
import * as FinderInput from '@packages/systems/design-system/FinderInput';
import * as Menu from '@packages/systems/design-system/Menu';
import {
  styled,
  styledDiv,
  useTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import {DeprecatedPopoverBridge} from '@packages/systems/design-system/PopoverBridge';
import {EnhancedScrollView} from '@packages/systems/design-system/ScrollView';
import {rightSidebarWidth} from '@packages/systems/design-system/constants/DesignerUI';
import PropertyList, {
  PropertyListNone,
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import type {CSSProperties} from '@packages/utilities/css-properties-type';
import {VisuallyHidden} from '@packages/systems/spring';

const MenuWrapper = styled(EnhancedScrollView)(
  ({theme: {gutters}, availableBottomHeight}) => ({
    maxHeight: `calc(100vh - ${(availableBottomHeight || 0) + gutters.x8}px)`,
    width: rightSidebarWidth,
    position: 'relative',
  })
);
const AllItemsWrapper = styledDiv(
  ({theme: {colors}}) => ({
    color: colors.foreground,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  }),
  {
    preselected: ({theme: {colors}}) => ({
      color: colors.white,
    }),
  }
);

const Divider = emotionStyled('div')({
  height: 1,
  width: '100%',
  backgroundColor: tokens.colors.border1,
  marginTop: tokens.space[1],
  marginBottom: tokens.space[1],
});

const StyledMenuItem = styled(Menu.Item)(
  ({theme: {fontSizes, gutters, control}, first, last}: any) => ({
    position: 'relative',
    width: `calc(100% - 2px)`,
    left: 1,
    marginTop: first ? gutters.x4 : undefined,
    marginBottom: last ? gutters.x4 : undefined,
    paddingTop: gutters.x4,
    paddingBottom: gutters.x4,
    fontSize: fontSizes.copy,
    minHeight: control.height,
    display: 'flex',
    alignItems: 'center',
  }),
  {
    preselected: ({theme: {colors}}) => ({
      background: colors.backgroundVividest,
    }),
    disabled: ({theme: {colors}}) => ({
      color: colors.foregroundDim,
    }),
  }
);

const EmptyItemsWrapper = emotionStyled('div')({
  padding: tokens.space[2],
});

const StyledPropertyListNone = styled(PropertyListNone)(
  ({theme: {colors, control}}) => ({
    border: `${control.borderWidth}px solid ${colors.border}`,
    borderRadius: control.borderRadius,
  })
);

const SearchWrapper = styled(FinderInput.Wrapper)({
  background: undefined,
  padding: 0,
  borderTop: 0,
  borderBottom: 0,
});

const SearchIcon = styled(FinderInput.SearchIcon)(
  ({theme: {gutters, colors}}) => ({
    left: gutters.x8,
    color: colors.foregroundDim,
  }),
  {
    focus: ({theme: {colors}}) => ({
      color: colors.foreground,
    }),
  }
);

const SearchCloseIconButton = emotionStyled(FinderInput.CloseIconButton)({
  '&': {
    right: '0px',
  },
});

const StyledFinePrint: React.ComponentType<{
  isSecondary?: boolean;
}> = styled(FinePrint)(
  ({theme: {colors}, isSecondary}: any) => ({
    display: 'inline',
    color: isSecondary ? colors.foregroundDim : colors.foreground,
  }),
  {
    preselected: ({theme: {colors}, isSecondary}: any) => ({
      color: isSecondary ? colors.foreground : colors.white,
    }),
  }
);

function showMaskConfig() {
  return [
    {
      id: 'search-input-list-popover',
      allowMouseEventsInWindow: true,
      isMask: false,
    },
  ];
}

type IdType = string;
export type ItemWithId<Item> = Item & {
  id: string;
};

type SearchInputPopoverListProps<Item> = {
  /** The list of records to match the query text against */
  searchList: Array<ItemWithId<Item>>;
  /** Configuration object defining options related to the "All items" item */
  allOptionConfig?: {
    /** Identifies the all option for the search and select functionality */
    id: IdType;
    /** Icon to render to the left of the All option */
    icon: React.ReactNode;
    /** Label for the all option menu item  */
    label: string;
  };
  /** Function to be called when an item from the search list is picked */
  onItemSelect: (arg1: ItemWithId<Item>) => void;
  /**
   * Function that is called on each item to determine which field(s) should be
   * searched. Eg if we want to search the text coming from `item.name`:
   *
   * ```js
   * const getSearchableText = item => item.name;
   * ```
   */
  getSearchableText: (arg1: ItemWithId<Item>) => string;
  /**
   * Render prop callback that renders a custom UI (usually a label) for each
   * matching item in the search result.
   */
  renderMatch?: (arg1: {
    preselected: ItemWithId<Item> | null;
    match: ItemWithId<Item>;
  }) => React.ReactElement;
  /** Defaults to "items".
   *
   * This prop allows for overriding the default "items" string render
   * in the default copy of menu items such as the "All items" and "No matching item"
   * menu options.
   *
   * This value is also used in data automation id's for some UI in this component
   */
  itemTypeLabel?: string;
  /** This prop enables overriding the text rendered when there's no match found using the search input
   *
   * Defaults to "No matching items" */
  noMatchLabel?: string;
  /** This prop enables overriding the text rendered when there's no additional matches when using the search input
   *
   * Defaults to "There are no items to add" */
  noAdditionalItemsLabel?: string;
  /** Placeholder to render within the input field.
   *
   * Defaults to "Search items..." or "Search {itemTypeLabel}..." if the `itemTypeLabel` prop was provided. */
  placeholder?: string;
  /** Enables the provided text to be hidden from view
   * while keeping the text visible to screenreaders for accessibility purposes */
  visuallyHiddenText?: string;
  /** This configuration object is only provided when we want this component
   * to also handle rendering the items selected by the popover */
  selectedItemsConfig?: {
    /** Name for this list of items */
    name: string;
    /** Function to be called with the ID of the item to be removed from the selection list */
    onItemRemoved: (arg1: string) => void;
    /** UI to render when there are no items currently selected */
    renderNone?: () => React.ReactElement;
    /** List of items currently selected and should be rendered */
    items: Array<ItemWithId<Item>>;
    /**
     * Callback that provides the selected item and the index of that item to render. */
    renderPropertyListItemLabel?: (
      arg1: ItemWithId<Item>,
      arg2: number
    ) => React.ReactElement;
  };
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const SearchInputPopoverList = <Item extends unknown>({
  searchList,
  onItemSelect,
  allOptionConfig,
  getSearchableText,
  renderMatch,
  itemTypeLabel = 'items',
  visuallyHiddenText,
  placeholder,
  noMatchLabel,
  noAdditionalItemsLabel,
  selectedItemsConfig,
}: SearchInputPopoverListProps<Item>) => {
  const [preselected, setPreselected] = React.useState<any>(null);
  const [searchText, setSearchText] = React.useState('');
  const {control, colors, gutters} = useTheme();
  const allItemsOption = React.useMemo<any>(
    () => (allOptionConfig ? {id: allOptionConfig.id} : null),
    [allOptionConfig]
  );

  const popoverHeightRef = React.useRef(null);
  // @ts-expect-error - TS7006 - Parameter 'el' implicitly has an 'any' type.
  const setPopoverHeightRef = React.useCallback((el) => {
    // This ensures that the list popover always renders beneath
    // the search input, no matter the viewport size.
    if (el && popoverHeightRef.current == null) {
      const bounds = el.getBoundingClientRect();
      popoverHeightRef.current = bounds.height + bounds.top;
    }
  }, []);

  const reduceItems = React.useCallback(
    // @ts-expect-error - TS7006 - Parameter 'items' implicitly has an 'any' type. | TS7006 - Parameter 'mapActions' implicitly has an 'any' type.
    (foundIndexes: Array<number>, items, mapActions) => {
      const results: Array<{
        actions: ItemActions;
        match: ItemWithId<Item> | any;
      }> = [];

      // When we have matches and not entered a query yet
      // show the "Add all" item as the first item
      if (
        allItemsOption &&
        foundIndexes.length > 0 &&
        searchText.length === 0
      ) {
        results.push({
          match: allItemsOption,
          actions: mapActions(allItemsOption),
        });
      }

      results.push(
        ...foundIndexes.map((index: number) => {
          return {
            match: items[index],
            actions: mapActions(items[index]),
          };
        })
      );

      return results;
    },
    [searchText, allItemsOption]
  );

  const [matches, {focus}, {onChange, onKeyDown, onFocus, onBlur, clear}] =
    useFuzzySearch({
      query: searchText,
      onQueryChange: setSearchText,
      items: searchList,
      getSearchableText,
      onSelectChange: onItemSelect,
      onPreselectChange: setPreselected,
      reduceItems,
    });

  const clearSearch = () => {
    clear();
    setPreselected(null);
  };

  return (
    <>
      <DeprecatedPopoverBridge
        dropdown
        borderWidth={control.borderWidth}
        borderColor={colors.logicFlows.blockBorderColor}
        placement="bottom"
        showMask={showMaskConfig}
        paddedMask={false}
        showArrow={false}
        metricsIdentifier="search-input-popover-list-popover"
        renderPopoverContent={({closePopover}) => (
          <MenuWrapper availableBottomHeight={popoverHeightRef.current}>
            <>
              {matches.length === 0 && searchList.length > 0 && (
                <EmptyItemsWrapper>
                  <StyledPropertyListNone
                    label={noMatchLabel || `No matching ${itemTypeLabel}`}
                  />
                </EmptyItemsWrapper>
              )}

              {searchList.length === 0 && (
                <EmptyItemsWrapper>
                  <StyledPropertyListNone
                    label={
                      noAdditionalItemsLabel ||
                      `There are no ${itemTypeLabel} to add`
                    }
                  />
                </EmptyItemsWrapper>
              )}

              {matches.length > 0 && (
                <Menu.Wrapper>
                  {matches.map(({match, actions}, i) => (
                    <React.Fragment
                      key={`${itemTypeLabel}-item-${match.id}-${i}`}
                    >
                      <StyledMenuItem
                        first={i === 0}
                        last={i === matches.length - 1}
                        data-automation-id={`${itemTypeLabel}-item-${i}`}
                        preselected={preselected === match}
                        // @ts-expect-error - error TS2322 - Type '(...args: unknown[]) => void' is not assignable to type '(e: MouseEvent<HTMLElement, MouseEvent>) => Promise<any> | undefined'.
                        onClick={chainFunctions(
                          actions.select,
                          clearSearch,
                          closePopover
                        )}
                        onMouseEnter={actions.preselect}
                      >
                        {/** Render the "All" option if the configuration for it was provided */}
                        {allOptionConfig && match.id === allOptionConfig.id ? (
                          <AllItemsWrapper preselected={preselected === match}>
                            <div
                              style={{
                                display: 'inline-block',
                                marginRight: '8px',
                              }}
                            >
                              {allOptionConfig.icon}
                            </div>
                            <StyledFinePrint
                              // @ts-expect-error [PDTY-2706] - TS2322 - Type not assignable to IntrinsicAttributes
                              preselected={preselected === match}
                            >
                              {allOptionConfig.label}
                            </StyledFinePrint>
                          </AllItemsWrapper>
                        ) : renderMatch ? (
                          renderMatch({preselected, match})
                        ) : (
                          <RenderMatch
                            highlight={preselected === match}
                            labels={[
                              {
                                label: getSearchableText(match),
                              },
                            ]}
                          />
                        )}
                      </StyledMenuItem>
                      {allOptionConfig && match.id === allOptionConfig.id && (
                        <Divider />
                      )}
                    </React.Fragment>
                  ))}
                </Menu.Wrapper>
              )}
            </>
          </MenuWrapper>
        )}
      >
        {({openPopover, Anchor}) => (
          <Anchor asChild>
            <div ref={setPopoverHeightRef}>
              <SearchWrapper>
                <SearchIcon focus={focus} />
                <FinderInput.Input
                  // HACK: to get around losing input focus when popover opens
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={focus}
                  onFocus={chainFunctions(onFocus, openPopover)}
                  onBlur={onBlur}
                  focus={focus}
                  value={searchText}
                  onChange={onChange}
                  onKeyDown={onKeyDown}
                  placeholder={placeholder || `Search ${itemTypeLabel}...`}
                  aria-describedby="visually-hidden-description"
                  data-automation-id={`search-input-popover-list-${itemTypeLabel}`}
                />
                {searchText && <SearchCloseIconButton onClick={clearSearch} />}
              </SearchWrapper>

              {visuallyHiddenText && (
                <VisuallyHidden id="visually-hidden-description">
                  {visuallyHiddenText}
                </VisuallyHidden>
              )}
            </div>
          </Anchor>
        )}
      </DeprecatedPopoverBridge>

      {selectedItemsConfig ? (
        <PropertyList
          // @ts-expect-error - TS2769 - No overload matches this call.
          name={selectedItemsConfig.name}
          onItemDeleted={selectedItemsConfig.onItemRemoved}
          renderNone={selectedItemsConfig.renderNone}
          items={selectedItemsConfig.items}
          style={{marginTop: gutters.x4}}
          renderItem={(match, index) => (
            <PropertyListItem
              data-automation-id={`PropertyListItem-${index}`}
              key={match.id}
              id={match.id}
              icon={null}
              label={
                selectedItemsConfig.renderPropertyListItemLabel
                  ? selectedItemsConfig.renderPropertyListItemLabel(
                      match,
                      index
                    )
                  : // If no `renderPropertyListItemLabel` prop is passed
                    // the only field we can for sure render is the id
                    match.id
              }
            />
          )}
        />
      ) : null}
    </>
  );
};

type RenderMatchLabels = Array<{
  /** Label to be shown */
  label: string;
  /** This prop indicates if this specific label is a secondary label.
   *
   * If true, the style for this label is shown in a dim font color
   * Otherwise, the style for the label is shown as a darker font color   */
  isSecondary?: boolean;
  /** Additional styling properties to be set for this specific label */
  style?: CSSProperties;
}>;
export function RenderMatch({
  labels,
  highlight,
  style,
}: {
  /** List of labels that should be rendered in the menu item for this match */
  labels: RenderMatchLabels;
  /** Indicates if this match should be highlighted */
  highlight: boolean;
  /** Styles to be applied to the container for this match */
  style?: CSSProperties;
}) {
  return (
    <span style={style}>
      {labels.map((option, index) => (
        <React.Fragment key={`{option.label}-${index}`}>
          <StyledFinePrint
            // @ts-expect-error [PDTY-2706] - TS2322 - Type not assignable to IntrinsicAttributes
            highlight={highlight}
            style={option.style}
            isSecondary={option.isSecondary}
          >
            {option.label}
          </StyledFinePrint>
        </React.Fragment>
      ))}
    </span>
  );
}

const InlineFinePrint = styled(FinePrint)(({theme: {gutters}}) => ({
  display: 'inline',
  marginLeft: gutters.x4,
}));

/** Helper function for rendering a primary and secondary label set side-by-side
   with our designated styling within the list of selected items */
export function RenderItemLabelHelper({
  primaryLabel,
  secondaryLabel,
}: {
  /** Label to shown on the left of the selected item */
  primaryLabel: string;
  /** Optional label to be shown on the right of the selected item  */
  secondaryLabel?: string;
}) {
  return (
    <span>
      {primaryLabel}{' '}
      {secondaryLabel && (
        <InlineFinePrint dim>{secondaryLabel}</InlineFinePrint>
      )}
    </span>
  );
}
